import { GROCERY_CATEGORIES, type GroceryCategory, type Ingredient } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, MessageSquarePlus, Pencil, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { NutriScoreBadge } from '@/features/catalog/badges';
import { findLabelConflicts } from '@recipes/domain';
import { removeProduct } from '@/features/catalog/catalog-queue';
import { syncCatalog } from '@/features/catalog/device-copy';
import { HintDialog, HintList } from '@/features/catalog/hint-dialog';
import {
  useCatalogBoot,
  useCatalogHints,
  useCatalogProduct,
  useShoppingAt,
} from '@/features/catalog/hooks';
import { NutritionTable, ProductCard } from '@/features/catalog/product-card';
import { ProductPrices } from '@/features/prices/product-prices';
import { AssignProductDialog } from '@/features/catalog/assign-product-dialog';
import { ProductForm } from '@/features/catalog/product-form';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { useProfile } from '@/features/shared/hooks';
import { groceryCategoryLabel } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, isVersionConflict, toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';

function IngredientEditor({ ingredient }: { ingredient: Ingredient }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ids = { name: useId(), category: useId(), alias: useId() };
  const [name, setName] = useState(ingredient.name);
  const [category, setCategory] = useState<GroceryCategory>(ingredient.groceryCategory);
  const [alias, setAlias] = useState('');
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all });
    void syncCatalog();
  };
  const save = useMutation({
    mutationFn: () =>
      api.ingredients.update(ingredient.id, {
        version: ingredient.version,
        name: name.trim(),
        groceryCategory: category,
      }),
    onSuccess: () => {
      toast.success(t('catalog.ingredients.saved'));
      refresh();
    },
    onError: (e) => (isVersionConflict(e) ? toast.error(t('catalog.conflict')) : toastError(e)),
  });
  const addAlias = useMutation({
    mutationFn: () => api.ingredients.addAlias(ingredient.id, { locale: 'de', name: alias.trim() }),
    onSuccess: () => {
      setAlias('');
      refresh();
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.ingredients.remove(ingredient.id),
    onSuccess: () => {
      toast.success(t('catalog.ingredients.deleted'));
      refresh();
      navigate('/catalog/ingredients');
    },
    onError: (e) => toastError(e),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={ids.name}>{t('catalog.ingredients.name')}</Label>
            <Input
              id={ids.name}
              className="mt-1"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={ids.category}>{t('shopping.item.category')}</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as GroceryCategory)}>
              <SelectTrigger id={ids.category} className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROCERY_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {groceryCategoryLabel(t, c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim()}>
            {t('common.save')}
          </Button>
          <Button variant="outline" onClick={() => setDeleting(true)}>
            <Trash2 aria-hidden /> {t('common.delete')}
          </Button>
        </div>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (alias.trim()) addAlias.mutate();
          }}
        >
          <div className="flex-1">
            <Label htmlFor={ids.alias}>{t('catalog.ingredients.addAlias')}</Label>
            <Input
              id={ids.alias}
              className="mt-1"
              maxLength={120}
              value={alias}
              placeholder={t('catalog.ingredients.aliasPlaceholder')}
              onChange={(e) => setAlias(e.target.value)}
              data-testid="ingredient-alias"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            loading={addAlias.isPending}
            disabled={!alias.trim()}
          >
            {t('common.add')}
          </Button>
        </form>
      </CardContent>
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('catalog.ingredients.deleteTitle', { name: ingredient.name })}
        description={t('catalog.ingredients.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </Card>
  );
}

export function CatalogIngredientPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const { store } = useShoppingAt();
  const [hintOpen, setHintOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  useCatalogBoot();

  const query = useQuery({
    queryKey: queryKeys.catalog.ingredient(id),
    queryFn: () => api.ingredients.get(id),
    enabled: Boolean(id),
  });
  const products = useQuery({
    queryKey: queryKeys.catalog.products({ ingredientId: id }),
    queryFn: () => api.products.list({ ingredientId: id, limit: 50 }),
    enabled: Boolean(id) && online,
  });
  const hints = useCatalogHints({ ingredientId: id });
  const ingredient = query.data;

  if (query.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (!ingredient)
    return (
      <PageContainer>
        <Alert variant="destructive">{describeError(query.error)}</Alert>
      </PageContainer>
    );
  const target = { ingredientId: ingredient.id, name: ingredient.name };

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={ingredient.name}
        description={[
          groceryCategoryLabel(t, ingredient.groceryCategory),
          ingredient.scope === 'GLOBAL' ? t('catalog.global') : t('catalog.own'),
        ].join(' · ')}
        backTo="/catalog/ingredients"
        actions={
          <Button onClick={() => setHintOpen(true)} data-testid="ingredient-add-hint">
            <MessageSquarePlus aria-hidden /> {t('catalog.hint.add')}
          </Button>
        }
      />
      <div className="space-y-5">
        {ingredient.aliases.length > 0 && (
          <p className="flex flex-wrap items-center gap-1 text-sm">
            <span className="text-muted-foreground">{t('catalog.ingredients.aliases')}:</span>
            {ingredient.aliases.map((alias) => (
              <Badge key={alias.id} variant="muted">
                {alias.name}
              </Badge>
            ))}
          </p>
        )}

        <section aria-labelledby="ingredient-hints">
          <h2 id="ingredient-hints" className="mb-2 font-semibold">
            {t('catalog.hint.title')}
          </h2>
          <HintList hints={hints} target={target} emptyText={t('catalog.hint.none')} />
        </section>

        {ingredient.nutrition && (
          <section>
            <NutritionTable
              values={ingredient.nutrition}
              facts={ingredient.nutrition}
              caption={t(`catalog.nutrition.${ingredient.nutrition.basis}`)}
            />
          </section>
        )}

        <section aria-labelledby="ingredient-products">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 id="ingredient-products" className="font-semibold">
              {t('catalog.tabs.products')}
            </h2>
            {online && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAssignOpen(true)}
                data-testid="ingredient-assign-product"
              >
                <Link2 aria-hidden /> {t('catalog.ingredients.assignProduct')}
              </Button>
            )}
          </div>
          {(products.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t('catalog.ingredients.noProducts')}</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {products.data!.items.map((product) => (
                <li key={product.id}>
                  <Link
                    to={`/catalog/products/${product.id}`}
                    className="flex min-h-[48px] items-center gap-3 px-3 py-2 hover:bg-accent/40"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {product.name}
                      {product.brand && (
                        <span className="text-muted-foreground"> · {product.brand}</span>
                      )}
                    </span>
                    {product.nutriScore && <NutriScoreBadge grade={product.nutriScore} size="sm" />}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-sm">
          <Link className="underline" to={`/recipes?q=${encodeURIComponent(ingredient.name)}`}>
            {t('catalog.ingredients.recipesLink', { name: ingredient.name })}
          </Link>
        </p>

        {ingredient.editable && online && <IngredientEditor ingredient={ingredient} />}
      </div>
      <HintDialog
        open={hintOpen}
        onOpenChange={setHintOpen}
        target={target}
        defaultStoreId={store?.id ?? null}
      />
      <AssignProductDialog
        ingredient={{ id: ingredient.id, name: ingredient.name }}
        open={assignOpen}
        onOpenChange={setAssignOpen}
      />
    </PageContainer>
  );
}

export function CatalogProductPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const { store } = useShoppingAt();
  const profile = useProfile();
  const [editing, setEditing] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useCatalogBoot();

  // The device copy answers first (and alone when offline); the API fills in the rest.
  const local = useCatalogProduct(id);
  const query = useQuery({
    queryKey: queryKeys.catalog.product(id),
    queryFn: () => api.products.get(id),
    enabled: Boolean(id) && online,
    retry: false,
  });
  const product = query.data ?? local ?? null;
  const hints = useCatalogHints({
    productId: id,
    ingredientId: product?.ingredient?.id ?? null,
  });
  const remove = useMutation({
    mutationFn: () => removeProduct(id),
    onSuccess: () => {
      toast.success(t('catalog.products.deleted'));
      void syncCatalog();
      navigate('/catalog/products');
    },
    onError: (e) => toastError(e),
  });

  if (!product) {
    return (
      <PageContainer>
        {query.isLoading || local === undefined ? (
          <Spinner label={t('app.loading')} />
        ) : (
          <Alert variant="destructive">
            {query.error ? describeError(query.error) : t('catalog.products.notOnDevice')}
          </Alert>
        )}
      </PageContainer>
    );
  }

  const conflicts = findLabelConflicts(product, {
    dietaryPreferences: profile.data?.dietaryPreferences ?? [],
    avoidedIngredients: profile.data?.avoidedIngredients ?? [],
  });

  return (
    <PageContainer className="max-w-xl">
      <FeaturePageHeader title={product.name} backTo="/catalog/products" />
      <Card>
        <CardContent className="p-4">
          {editing ? (
            <ProductForm
              product={product}
              onCancel={() => setEditing(false)}
              onSaved={(saved) => {
                setEditing(false);
                toast.success(t('catalog.products.saved'));
                // Correcting a global row continues on the household's own copy.
                if (saved.id !== product.id)
                  navigate(`/catalog/products/${saved.id}`, { replace: true });
              }}
            />
          ) : (
            <ProductCard
              product={product}
              hints={hints}
              conflicts={conflicts}
              store={store}
              actions={
                <>
                  <Button onClick={() => setHintOpen(true)} data-testid="product-add-hint">
                    <MessageSquarePlus aria-hidden /> {t('catalog.hint.add')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    data-testid="product-edit"
                  >
                    <Pencil aria-hidden /> {t('catalog.product.correct')}
                  </Button>
                  {product.editable && (
                    <Button variant="ghost" disabled={!online} onClick={() => setDeleting(true)}>
                      <Trash2 aria-hidden /> {t('common.delete')}
                    </Button>
                  )}
                </>
              }
            />
          )}
        </CardContent>
      </Card>
      {!editing && (
        <div className="mt-6">
          <ProductPrices product={product} />
        </div>
      )}
      <HintDialog
        open={hintOpen}
        onOpenChange={setHintOpen}
        target={{ productId: product.id, name: product.name }}
        defaultStoreId={store?.id ?? null}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('catalog.products.deleteTitle', { name: product.name })}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </PageContainer>
  );
}
